import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { RouteErrorElement } from '@/components/ui/RouteErrorElement';
import { env } from '@/config/env';
import { ApiKeyPage } from '@/features/api-key/ApiKeyPage';
import { AppDetailPage } from '@/features/apps/AppDetailPage';
import { AppsPage } from '@/features/apps/AppsPage';
import { NotFoundPage } from '@/features/not-found/NotFoundPage';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <RouteErrorElement />,
    children: [
      { index: true, element: <Navigate to="apps" replace /> },
      { path: 'apps', element: <AppsPage /> },
      { path: 'apps/:appId', element: <AppDetailPage /> },
      { path: 'api-key', element: <ApiKeyPage /> },
      // Catch-all: render the in-shell not-found page instead of throwing a 404 that
      // the errorElement would surface as a generic crash.
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

// Cold-load deep link: Prism embeds us at the iframe root and passes the intended
// inner route as `?route=/api-key` (derived from its #/developer_portal/<route> hash).
// Seed the router there before first paint so a shared link opens the right screen.
const initialRoute = new URLSearchParams(window.location.search).get('route');
if (initialRoute && initialRoute.startsWith('/') && initialRoute !== '/') {
  void router.navigate(initialRoute, { replace: true });
}

// Mirror inner navigations up to Prism so its address bar (and any copied link)
// tracks the current screen. Prism rewrites its #/developer_portal/<path> hash from
// this. Only when embedded; posts the settled location once navigation is idle.
function isEmbedded(): boolean {
  try {
    return window.parent !== window.self;
  } catch {
    return true;
  }
}

if (isEmbedded()) {
  let lastPath: string | null = null;
  router.subscribe((state) => {
    if (state.navigation.state !== 'idle') return;
    const path = state.location.pathname + state.location.search;
    if (path === lastPath) return;
    lastPath = path;
    // Post to the known Prism origins rather than '*': postMessage only delivers to a
    // parent whose origin matches, so a non-Prism embedder never receives the route.
    for (const origin of env.prismAllowedOrigins) {
      window.parent.postMessage({ method: 'routeChanged', params: { path } }, origin);
    }
  });

  // Parent-driven navigation: Prism forwards a `navigate` when its hash changes
  // externally (URL-bar edit). Origin-gated like the auth bridge; de-duped so it
  // doesn't fight the mirror above.
  window.addEventListener('message', (event) => {
    if (!env.prismAllowedOrigins.includes(event.origin)) return;
    const data = event.data as { method?: string; params?: { path?: string } } | null;
    if (!data || data.method !== 'navigate') return;
    const path = data.params?.path;
    if (typeof path !== 'string' || !path.startsWith('/')) return;
    const current = router.state.location.pathname + router.state.location.search;
    if (path !== current) void router.navigate(path);
  });
}
